import { CompanySettings } from "@/types/firebase";

/**
 * Determines whether ACH payments are available for the given company.
 *
 * The logic checks the modern multi-gateway configuration first, falls back to legacy
 * payment flags, and finally verifies that legacy WikiPayit credentials exist.
 */
export function isAchPaymentAvailable(company?: CompanySettings | null): boolean {
  if (!company) {
    return false;
  }

  // Check for explicit ACH disabled flag (set when merchant goes live)
  const paymentGateways = company.paymentGateways as {
    achEnabled?: boolean;
    providers?: Record<string, { enabled?: boolean }>;
    defaults?: { ach?: string };
  } | undefined;

  if (paymentGateways?.achEnabled === false) {
    return false;
  }

  const legacyPaymentSettings = (company as { payment?: { achEnabled?: boolean } })?.payment;
  // Check for legacy ACH disabled flag
  if (legacyPaymentSettings?.achEnabled === false) {
    return false;
  }
  // Check for legacy ACH enabled flag
  if (legacyPaymentSettings?.achEnabled === true) {
    return true;
  }

  if (paymentGateways) {
    const providers = paymentGateways.providers || {};
    const isProviderEnabled = (provider?: { enabled?: boolean }) => provider?.enabled ?? false;
    const defaultAch = paymentGateways.defaults?.ach;

    if (defaultAch === "nmi") {
      // Slider explicitly enables NMI for ACH. Ensure credentials exist for legacy support.
      const wikiPayitSettings = (company as { wikiPayitSettings?: { tokenizationKey?: string; securityKey?: string } }).wikiPayitSettings;
      if (wikiPayitSettings?.tokenizationKey && wikiPayitSettings?.securityKey) {
        return true;
      }

      // Fallback to provider toggle if credentials are missing (future-proof, e.g. dedicated ACH flag).
      return isProviderEnabled(providers.nmi);
    }

    if (defaultAch === "payarc") {
      // PayArc ACH support is not yet available - always return false for now
      return false;
    }

    // No explicit default set – enable ACH if NMI is enabled
    // Note: PayArc does not support ACH at this time
    return isProviderEnabled(providers.nmi);
  }

  const wikiPayitSettings = (company as { wikiPayitSettings?: { tokenizationKey?: string; securityKey?: string } }).wikiPayitSettings;
  if (wikiPayitSettings?.tokenizationKey && wikiPayitSettings?.securityKey) {
    return true;
  }

  return false;
}
